import { useCallback, useEffect, useState } from 'react';
import { Link2, Send, Copy, XCircle, CheckCircle2, Clock, Eye, MessageCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import PaymentLinkModal from './PaymentLinkModal';
import { commerceCall as call, isLinkOpen as isOpen, type LinkCapabilities as Capabilities, type PaymentLink } from './paymentLinkApi';

/* Collect a sale's balance online: the link modal generates (or reopens) the
   link to copy or send on WhatsApp (only when the gym's `payment_link`
   template is enabled); this panel shows what happened to it. Renders nothing
   for a gym that has not turned online payments on — a desk-only gym sees no
   change. */

const money = (n: number) => `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const when = (iso: string) => new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

function StatusChip({ link }: { link: PaymentLink }) {
  const expired = !isOpen(link) && (link.status === 'created' || link.status === 'sent');
  const s = expired ? 'expired' : link.status;
  const styles: Record<string, string> = {
    paid: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    sent: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    created: 'bg-slate-50 text-slate-700 border-slate-200',
    expired: 'bg-amber-50 text-amber-700 border-amber-200',
    cancelled: 'bg-slate-50 text-slate-500 border-slate-200',
  };
  const label = s === 'sent' && link.openedAt ? 'Opened' : s === 'created' ? 'Not sent' : s[0].toUpperCase() + s.slice(1);
  return <span className={`px-2 py-0.5 rounded-md border text-[10px] font-black uppercase tracking-widest ${styles[s]}`}>{label}</span>;
}

export default function PaymentLinkPanel({ gymId, memberId, purchaseId, pendingAmount, memberName, memberPhone }: {
  gymId: string;
  memberId: string;
  purchaseId: string;
  pendingAmount: number;
  memberName?: string | null;
  memberPhone?: string | null;
}) {
  const [caps, setCaps] = useState<Capabilities | null>(null);
  const [links, setLinks] = useState<PaymentLink[]>([]);
  const [busy, setBusy] = useState<null | 'cancel'>(null);
  const [showModal, setShowModal] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, l] = await Promise.all([
        call<Capabilities>(`/capabilities?gymId=${gymId}`),
        call<PaymentLink[]>(`/payment-requests?gymId=${gymId}&purchaseId=${purchaseId}`),
      ]);
      setCaps(c);
      setLinks(l);
    } catch {
      setCaps(null); // fail quiet: this panel is additive, never block the invoice
    }
  }, [gymId, purchaseId]);

  useEffect(() => { load(); }, [load]);

  if (!caps?.onlinePayments || pendingAmount <= 0) return null;

  const live = links.find(isOpen);
  const history = links.filter(l => l !== live);

  const cancel = async (link: PaymentLink) => {
    if (!window.confirm('Cancel this payment link? The member will no longer be able to pay with it.')) return;
    setBusy('cancel');
    try {
      await call(`/payment-requests/${link.id}/cancel`, { method: 'POST', body: JSON.stringify({ gymId }) });
      toast.success('Payment link cancelled');
      await load();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusy(null);
    }
  };

  const copy = async (url: string) => {
    await navigator.clipboard.writeText(url);
    toast.success('Payment link copied');
  };

  return (
    <div className="px-4 sm:px-10 py-4 bg-white border-b border-slate-100 shrink-0">
      <div className="flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-6">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-xl bg-indigo-50 flex items-center justify-center shrink-0">
            <Link2 className="w-4 h-4 text-indigo-700" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-bold text-slate-900">Collect {money(pendingAmount)} online</p>
            {live ? (
              <div className="flex items-center gap-2 flex-wrap mt-0.5">
                <StatusChip link={live} />
                <span className="text-xs text-slate-500">
                  {live.lastSentAt ? `Sent ${when(live.lastSentAt)}${live.sendCount > 1 ? ` · ${live.sendCount}×` : ''}` : `Created ${when(live.createdAt)}`}
                  {' · '}valid until {new Date(live.expiresAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                </span>
              </div>
            ) : (
              <p className="text-xs text-slate-500 mt-0.5">Send the member a secure link to pay by UPI, card or netbanking.</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 lg:ml-auto flex-wrap">
          {live ? (
            <>
              <button onClick={() => setShowModal(true)} className="px-4 py-2 rounded-xl bg-indigo-700 text-white text-xs font-black uppercase tracking-widest flex items-center gap-2">
                <Send className="w-3.5 h-3.5" /> {caps.canSendLinkOnWhatsApp && live.sendCount > 0 ? 'Send again' : 'Open link'}
              </button>
              <button onClick={() => copy(live.url)} className="px-4 py-2 rounded-xl border border-slate-200 text-slate-700 text-xs font-black uppercase tracking-widest flex items-center gap-2 hover:bg-slate-50">
                <Copy className="w-3.5 h-3.5" /> Copy link
              </button>
              <button onClick={() => cancel(live)} disabled={!!busy} className="p-2 rounded-xl text-slate-400 hover:text-rose-600 hover:bg-rose-50 disabled:opacity-50" title="Cancel link">
                <XCircle className="w-4 h-4" />
              </button>
            </>
          ) : (
            <button onClick={() => setShowModal(true)} className="px-4 py-2 rounded-xl bg-indigo-700 text-white text-xs font-black uppercase tracking-widest flex items-center gap-2">
              <Link2 className="w-3.5 h-3.5" /> {links.length > 0 ? 'Generate new link' : 'Generate payment link'}
            </button>
          )}
        </div>
      </div>

      {!caps.canSendLinkOnWhatsApp && caps.missing.includes('payment_link_template') && (
        <p className="mt-2 text-xs text-slate-500 flex items-center gap-1.5">
          <MessageCircle className="w-3.5 h-3.5 shrink-0" />
          To send links on WhatsApp, the “Payment link” message needs to be enabled for your gym. Contact Gympe support. You can still copy the link and share it.
        </p>
      )}

      {(live?.messages?.length || history.length > 0) && (
        <details className="mt-3 group">
          <summary className="text-xs font-bold text-slate-500 cursor-pointer select-none">Link history</summary>
          <ul className="mt-2 space-y-1.5">
            {[...(live ? [live] : []), ...history].map(l => (
              <li key={l.id} className="text-xs text-slate-600">
                <div className="flex items-center gap-2 flex-wrap">
                  <StatusChip link={l} />
                  <span className="font-semibold">{money(l.paidAmount ?? l.amount)}</span>
                  <span className="text-slate-400">created {when(l.createdAt)}</span>
                  {l.openedAt && <span className="flex items-center gap-1 text-slate-400"><Eye className="w-3 h-3" />opened {when(l.openedAt)}</span>}
                  {l.paidAt && <span className="flex items-center gap-1 text-emerald-600"><CheckCircle2 className="w-3 h-3" />paid {when(l.paidAt)}</span>}
                  {l.excessAmount > 0 && <span className="text-rose-600 font-semibold">{money(l.excessAmount)} overpaid — refund due</span>}
                </div>
                {(l.messages ?? []).map((m, i) => (
                  <div key={i} className="pl-4 mt-0.5 flex items-center gap-1.5 text-slate-400">
                    <Clock className="w-3 h-3" />
                    WhatsApp {m.status.toLowerCase()} {when(m.createdAt)}{m.errorMessage ? ` — ${m.errorMessage}` : ''}
                  </div>
                ))}
              </li>
            ))}
          </ul>
        </details>
      )}

      {showModal && (
        <PaymentLinkModal
          gymId={gymId}
          memberId={memberId}
          purchaseId={purchaseId}
          memberName={memberName}
          memberPhone={memberPhone}
          onClose={() => { setShowModal(false); load(); }}
        />
      )}
    </div>
  );
}
